import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Box,
  Dialog,
  DialogContent,
  ImageList,
  ImageListItem,
  Modal,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Paper,
  Fade,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { config } from '../config';
import { getDerivativeImageUrl, getImageUrl } from '../utils/imageUrl';

const MotionBox = motion(Box);

function Gallery() {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(true);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isTouchZoomed, setIsTouchZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [touchStartTime, setTouchStartTime] = useState(0);
  const [touchPosition, setTouchPosition] = useState({ x: 0, y: 0 });
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (remainingSeconds > 0 || parts.length === 0) parts.push(`${remainingSeconds}s`);
    
    return parts.join(' ');
  };

  useEffect(() => {
    fetchImages();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const imageId = params.get('image');
    if (imageId && images.length > 0) {
      const image = images.find(img => img.id === imageId);
      if (image) {
        setSelectedImage(image);
      }
    }
  }, [location.search, images]);

  const fetchImages = async () => {
    try {
      const response = await fetch(config.endpoints.gallery);
      const data = await response.json();
      setImages(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching images:', error);
      setLoading(false);
    }
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setShowDetails(true); // Always show details when opening a new image
  };

  const handleClose = () => {
    setSelectedImage(null);
    setShowDetails(true);
    navigate('/gallery');
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const parseDescription = (description) => {
    if (!description) return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(description, 'text/html');
    return doc.body.textContent || '';
  };

  const handleImageMouseDown = (e) => {
    if (isMobile) return;
    e.preventDefault();
    setIsZoomed(true);
    updateZoomPosition(e);
  };

  const handleImageMouseUp = () => {
    if (isMobile) return;
    setIsZoomed(false);
  };

  const handleImageMouseMove = (e) => {
    if (isMobile) return;
    if (isZoomed) {
      updateZoomPosition(e);
    }
  };

  const handleImageMouseLeave = () => {
    if (isMobile) return;
    setIsZoomed(false);
  };

  const handleTouchStart = (e) => {
    if (!isMobile) return;
    setTouchStartTime(Date.now());
    const touch = e.touches[0];
    setTouchPosition({
      x: touch.clientX,
      y: touch.clientY
    });
  };

  const handleTouchMove = (e) => {
    if (!isMobile || !isTouchZoomed) return;
    e.preventDefault();
    const touch = e.touches[0];
    const rect = e.target.getBoundingClientRect();
    const x = ((touch.clientX - rect.left) / rect.width) * 100;
    const y = ((touch.clientY - rect.top) / rect.height) * 100;
    setZoomPosition({ x, y });
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;
    const touchDuration = Date.now() - touchStartTime;
    if (touchDuration > 200) {
      // Long press detected
      setIsTouchZoomed(false);
    }
  };

  const updateZoomPosition = (e) => {
    const rect = e.target.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPosition({ x, y });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        py: 4,
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        Gallery
      </Typography>

      <ImageList 
        variant="masonry" 
        cols={isMobile ? 1 : 3} 
        gap={8}
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        {images.map((image) => (
          <ImageListItem 
            key={image.id}
            onClick={() => handleImageClick(image)}
            sx={{ cursor: 'pointer' }}
          >
            <img
              src={image.thumbnailUrl || getDerivativeImageUrl(image.url, 'thumb')}
              srcSet={`${image.thumbnailUrl || getDerivativeImageUrl(image.url, 'thumb')} 640w, ${image.mediumUrl || getDerivativeImageUrl(image.url, 'medium')} 1800w`}
              sizes={isMobile ? '100vw' : '33vw'}
              alt={image.title || ''}
              loading="lazy"
              decoding="async"
              style={{ objectFit: 'cover' }}
            />
            {image.description && (
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'rgba(0, 0, 0, 0.8)',
                  borderRadius: '0 0 8px 8px',
                  color: 'white'
                }}
              >
                <Typography
                  variant="body2"
                  dangerouslySetInnerHTML={{ __html: image.description }}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                  }}
                />
              </Paper>
            )}
          </ImageListItem>
        ))}
      </ImageList>

      <Dialog
        open={Boolean(selectedImage)}
        onClose={handleClose}
        maxWidth={false}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: '#000000',
            color: 'white',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }
        }}
      >
        <DialogContent 
          sx={{ 
            p: 0, 
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            position: 'relative'
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'fixed',
              right: 24,
              top: 24,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 1,
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <Fade in timeout={400}>
              <Box 
                sx={{ 
                  position: 'relative',
                  width: '90%',
                  height: '90%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'scaleIn 0.4s ease-out',
                  '@keyframes scaleIn': {
                    '0%': {
                      transform: 'scale(0.9)',
                      opacity: 0
                    },
                    '100%': {
                      transform: 'scale(1)',
                      opacity: 1
                    }
                  }
                }}
              >
                <Box
                  component="img"
                  src={(isZoomed || isTouchZoomed) ? getImageUrl(selectedImage.originalUrl || selectedImage.url) : (selectedImage.mediumUrl || getDerivativeImageUrl(selectedImage.url, 'medium'))}
                  data-original-src={getImageUrl(selectedImage.originalUrl || selectedImage.url)}
                  alt={selectedImage.title || ''}
                  onMouseDown={handleImageMouseDown}
                  onMouseUp={handleImageMouseUp}
                  onMouseMove={handleImageMouseMove}
                  onMouseLeave={handleImageMouseLeave}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    display: 'block',
                    borderRadius: '8px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                    cursor: (isZoomed || isTouchZoomed) ? 'zoom-out' : 'zoom-in',
                    transition: 'transform 0.1s ease-out',
                    transform: (isZoomed || isTouchZoomed) ? 'scale(2.5)' : 'scale(1)',
                    transformOrigin: (isZoomed || isTouchZoomed) ? `${zoomPosition.x}% ${zoomPosition.y}%` : 'center',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    touchAction: isTouchZoomed ? 'none' : 'auto'
                  }}
                />
                {(selectedImage.telescope || selectedImage.mount || selectedImage.camera || selectedImage.filter || selectedImage.duration) && (
                  <Fade in={showDetails} timeout={200}>
                    <Paper
                      sx={{
                        position: 'absolute',
                        bottom: '5%',
                        right: '5%',
                        bgcolor: 'rgba(0, 0, 0, 0.75)',
                        backdropFilter: 'blur(10px)',
                        p: 2,
                        borderRadius: 2,
                        maxWidth: 300,
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.3)',
                        display: showDetails ? 'block' : 'none'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6" sx={{ 
                          color: 'primary.main',
                          fontSize: '1rem',
                          fontWeight: 500
                        }}>
                          Technical Details
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={toggleDetails}
                          sx={{ 
                            color: 'white',
                            p: 0.5,
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.1)'
                            }
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {selectedImage.telescope && (
                        <Typography variant="body2" sx={{ mb: 0.75, color: 'rgba(255, 255, 255, 0.9)' }}>
                          <strong style={{ color: 'white' }}>Telescope:</strong> {selectedImage.telescope}
                        </Typography>
                      )}
                      {selectedImage.mount && (
                        <Typography variant="body2" sx={{ mb: 0.75, color: 'rgba(255, 255, 255, 0.9)' }}>
                          <strong style={{ color: 'white' }}>Mount:</strong> {selectedImage.mount}
                        </Typography>
                      )}
                      {selectedImage.camera && (
                        <Typography variant="body2" sx={{ mb: 0.75, color: 'rgba(255, 255, 255, 0.9)' }}>
                          <strong style={{ color: 'white' }}>Camera:</strong> {selectedImage.camera}
                        </Typography>
                      )}
                      {selectedImage.filter && (
                        <Typography variant="body2" sx={{ mb: 0.75, color: 'rgba(255, 255, 255, 0.9)' }}>
                          <strong style={{ color: 'white' }}>Filter:</strong> {selectedImage.filter}
                        </Typography>
                      )}
                      {selectedImage.duration && (
                        <Typography variant="body2" sx={{ mb: 0.75, color: 'rgba(255, 255, 255, 0.9)' }}>
                          <strong style={{ color: 'white' }}>Duration:</strong> {formatDuration(parseInt(selectedImage.duration, 10))}
                        </Typography>
                      )}
                    </Paper>
                  </Fade>
                )}
                {!showDetails && (selectedImage.telescope || selectedImage.mount || selectedImage.camera || selectedImage.filter || selectedImage.duration) && (
                  <Tooltip title="Show Technical Details" arrow>
                    <IconButton
                      onClick={toggleDetails}
                      sx={{
                        position: 'absolute',
                        bottom: '5%',
                        right: '5%',
                        color: 'white',
                        bgcolor: 'rgba(0, 0, 0, 0.75)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.9)'
                        }
                      }}
                    >
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Fade>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}

export default Gallery;
